<template>
    <div class="overflow-hidden rounded-lg border border-gray-200">
        <!-- Header -->
        <button
            type="button"
            class="flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
            @click="expandedSection = !expandedSection"
        >
            <div class="flex items-center space-x-2">
                <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-100">
                    <font-awesome-icon icon="fa-solid fa-clock-rotate-left" class="text-xs text-amber-600" />
                </span>
                <span class="text-sm font-semibold text-gray-700">Histórico de Configurações Financeiras</span>
                <span v-if="!loading" class="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                    {{ historyCount }} {{ historyCount === 1 ? 'registro' : 'registros' }}
                </span>
            </div>
            <font-awesome-icon
                icon="fa-solid fa-chevron-down"
                class="text-xs text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-180': expandedSection }"
            />
        </button>

        <div v-show="expandedSection" class="border-t border-gray-200">
            <!-- Loading -->
            <div v-if="loading" class="flex justify-center p-6">
                <svg
                    aria-hidden="true"
                    class="h-8 w-8 animate-spin text-gray-200 fill-blue-600"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                    />
                    <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                    />
                </svg>
            </div>

            <!-- Empty state -->
            <div v-else-if="historyCount === 0" class="p-4">
                <p class="text-sm text-gray-500">Nenhum registro de alteração encontrado.</p>
            </div>

            <!-- Timeline -->
            <div v-else class="p-4">
                <ol class="relative ml-3 border-l border-gray-200 dark:border-gray-700">
                    <li
                        v-for="(history, idx) in history_data"
                        :key="idx"
                        class="mb-8 ml-4 last:mb-0"
                    >
                        <div
                            class="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-white bg-amber-500 dark:border-gray-900"
                        ></div>
                        <div class="flex items-start justify-between">
                            <div>
                                <time class="text-xs font-normal text-gray-400 dark:text-gray-500">
                                    {{ history.created_at }}
                                </time>
                                <p class="mt-0.5 text-sm font-semibold text-gray-900 dark:text-white">
                                    {{ history.observation }}
                                </p>
                                <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                    por {{ history.user.name }} ({{ history.user.email }})
                                </p>
                            </div>
                            <button
                                type="button"
                                class="ml-2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                @click="openModalInfo(idx)"
                            >
                                <font-awesome-icon
                                    icon="fa-solid fa-circle-info"
                                    class="h-4 w-4"
                                />
                            </button>
                        </div>
                    </li>
                </ol>

                <div v-if="hasMore" class="mt-4 flex justify-center">
                    <button
                        type="button"
                        class="rounded-md border border-gray-200 bg-white px-4 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
                        :disabled="loadingMore"
                        @click="loadMore"
                    >
                        <span v-if="loadingMore">Carregando...</span>
                        <span v-else>Carregar mais</span>
                    </button>
                </div>
            </div>
        </div>

        <financial-log-info-modal
            v-if="financial_info_modal"
            :history="history_selected"
            @closeModal="closeModal()"
        />
    </div>
</template>

<script>
import HelperMixin from "../../../mixins/HelperMixin.vue";
import FinancialLogInfoModal from "../modals/FinancialLogInfoModal.vue";

export default {
    props: ["user"],
    mixins: [HelperMixin],
    components: {
        FinancialLogInfoModal,
    },
    data() {
        return {
            loading: false,
            loadingMore: false,
            expandedSection: false,
            history_data: [],
            history_selected: {},
            financial_info_modal: false,
            current_page: 0,
            last_page: 1,
            total: 0,
        };
    },
    computed: {
        historyCount() {
            return this.total || this.history_data.length;
        },
        hasMore() {
            return this.current_page < this.last_page;
        },
    },
    mounted() {
        this.fetch();
    },
    methods: {
        fetch(page = 1) {
            const isInitial = page === 1;
            if (isInitial) {
                this.loading = true;
            } else {
                this.loadingMore = true;
            }

            axios
                .get("/api/user/" + this.user.id + "/settings/history", { params: { page } })
                .then(({ data }) => {
                    const items = (data.data || []).filter(
                        (item) => item.properties?.type !== 'fee_tier'
                    );

                    this.history_data = isInitial ? items : [...this.history_data, ...items];
                    this.current_page = data.current_page || page;
                    this.last_page = data.last_page || 1;
                    this.total = data.total || 0;
                })
                .finally(() => {
                    this.loading = false;
                    this.loadingMore = false;
                });
        },
        loadMore() {
            this.fetch(this.current_page + 1);
        },
        openModal() {
            this.financial_info_modal = true;
        },
        closeModal() {
            this.financial_info_modal = false;
        },
        openModalInfo(idx) {
            this.history_selected = this.history_data[idx];
            this.financial_info_modal = true;
        },
    },
};
</script>
